import React from "react";
import { Box } from "@mui/material";
import { useWindowSize } from "@uidotdev/usehooks";
import SabiasQue from "../SabiasQue";

// Template113: Sky Garden, Leadenhall Market, Catedral de San Pablo, etc.
// Adaptado del componente Page11.jsx de guia-londres. Secciones separadas (sin bucle).
const Template113 = ({
	backgroundColor,
	textColor,
	headerBackgroundColor,
	headerTextColor,
	fontFamily,
	page,
	updatePage,
}) => {
	const size = useWindowSize();
	const isSmallScreen = size.width <= 600;

	const headerText = page.texts?.[0]?.content;
	const logoImage =
		page.canvasImages?.find((img) => img.id === "logo")?.imageUrl ||
		"/images/logo.png";

	const getImageSrc = (id) => {
		if (page.images && Array.isArray(page.images)) {
			const found = page.images.find((img) => img?.id === id);
			if (found && found.src) return found.src;
		}
		return undefined;
	};

	const getCanvasImageUrl = (id) => {
		if (page.canvasImages && Array.isArray(page.canvasImages)) {
			const found = page.canvasImages.find((img) => img?.id === id);
			if (found && found.imageUrl) return found.imageUrl;
		}
		return undefined;
	};

	const sections = Array.isArray(page?.sections)
		? page.sections
		: Array.isArray(page?.data?.sections)
			? page.data.sections
			: Array.isArray(page?.data?.data?.sections)
				? page.data.data.sections
				: [];
	const ui = page?.ui ?? page?.data?.ui ?? page?.data?.data?.ui ?? {};
	const skyGarden = sections[0];
	const leadenhall = sections[1];
	const sanPablo = sections[2];
	const puente = sections[3];
	const tateModern = sections[4];
	const shakespeare = sections[5];

	const skyGardenSticker = getCanvasImageUrl("sky-garden");
	const leadenhallImage = getImageSrc("leadenhall");
	const puenteImage = getImageSrc("puente");
	const shakespeareImage = getImageSrc("shakespeare");

	let actualHeaderColor = headerBackgroundColor || "#de5624";
	const contentColor = textColor || "#444";

	const h3 = {
		fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
		fontSize: 14,
		fontWeight: 700,
		textTransform: "uppercase",
		margin: 0,
	};

	const p = {
		fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
		fontSize: 13,
		color: contentColor,
		margin: 0,
	};

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				minHeight: "100%",
				height: "auto",
				width: "100%",
				backgroundColor: backgroundColor || "#f7f7f7",
				color: contentColor,
				position: "relative",
				fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
				"& *:not([data-template-header])": {
					fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
					color: "inherit",
				},
			}}
		>
			<Box
				data-block-id="header"
				data-template-header
				sx={{
					backgroundColor: actualHeaderColor,
					color: headerTextColor || "#fff",
					textAlign: "center",
					padding: "15px 0",
					fontFamily: `${fontFamily || "'Poppins', sans-serif"}`,
					fontSize: "14px",
					fontWeight: 600,
					flexShrink: 0,
					position: "sticky",
					top: 0,
					zIndex: 999999,
				}}
			>
				{headerText}
				{logoImage && (
					<Box
						component="img"
						src={logoImage}
						alt="logo"
						sx={{
							width: isSmallScreen ? 66 : 80,
							position: "absolute",
							right: 2,
							top: 18,
							zIndex: 999999,
						}}
					/>
				)}
			</Box>

			<Box
				sx={{
					padding: "10px 12px",
					display: "flex",
					flexDirection: "column",
					gap: "18px",
					marginTop: "8px",
				}}
			>
				{/* Sky Garden */}
				{(skyGarden?.title || skyGarden?.body) && (
					<Box data-block-id="text-1" sx={{ display: "flex", gap: "16px", position: "relative" }}>
						<Box sx={{ position: "absolute", top: -10, left: "35%" }}>
							{skyGarden?.sabiasQue?.[0] && (
								<SabiasQue
									icono={skyGarden.sabiasQue[0].icono}
									texto={skyGarden.sabiasQue[0].texto}
								/>
							)}
						</Box>
						<Box sx={{ position: "absolute", top: -10, left: "45%" }}>
							{skyGarden?.sabiasQue?.[1] && (
								<SabiasQue
									icono={skyGarden.sabiasQue[1].icono}
									texto={skyGarden.sabiasQue[1].texto}
								/>
							)}
						</Box>
						<Box sx={{ width: "60%", position: "relative" }}>
							{skyGarden?.title && (
								<Box component="h3" sx={h3}>
									{skyGarden.title}
								</Box>
							)}
							<Box component="p" sx={p}>
								{skyGarden?.body}
							</Box>
						</Box>
						<Box component="p" sx={{ ...p }}>
							{skyGarden?.reserva && (
								<>
									<Box
										sx={{
											position: "absolute",
											bottom: isSmallScreen ? 40 : 14,
											right: isSmallScreen ? -10 : 0,
											backgroundColor: "#fff8db",
											border: "2px solid #f5c400",
											borderRadius: "16px 16px 0px 16px",
											padding: "10px 10px",
											fontSize: 11,
											fontFamily: `${fontFamily || "'Poppins', sans-serif"}`,
											color: contentColor,
											boxShadow: "2px 2px 4px rgba(0,0,0,0.15)",
											maxWidth: isSmallScreen ? 140 : 200,
											zIndex: 6,
										}}
									>
										{skyGarden.reserva.title && (
											<strong>{skyGarden.reserva.title}</strong>
										)}{" "}
										{isSmallScreen
											? skyGarden.reserva.bodyShort
											: skyGarden.reserva.bodyLong}
									</Box>
									{skyGardenSticker && (
										<img
											src={skyGardenSticker}
											alt=""
											style={{
												width: 100,
												position: "absolute",
												bottom: isSmallScreen ? -10 : -40,
												right: isSmallScreen ? -20 : 0,
												zIndex: 5,
											}}
										/>
									)}
								</>
							)}
						</Box>
					</Box>
				)}

				{/* Leadenhall Market */}
				{(leadenhall?.title || leadenhall?.body) && (
					<Box data-block-id="text-2" sx={{ display: "flex", gap: "16px", position: "relative" }}>
						{leadenhallImage && (
							<img
								src={leadenhallImage}
								alt={leadenhall?.title}
								style={{
									width: "40%",
									height: 110,
									borderRadius: 12,
									objectFit: "cover",
								}}
							/>
						)}
						<Box sx={{ width: "60%", position: "relative" }}>
							{leadenhall?.title && (
								<Box component="h3" sx={h3}>
									{leadenhall.title}
								</Box>
							)}
							{leadenhall?.body && (
								<Box component="p" sx={p}>
									{leadenhall.body}
								</Box>
							)}
						</Box>
						{leadenhall?.sabiasQue?.[0] && (
							<Box
								sx={{
									position: "absolute",
									bottom: isSmallScreen ? 0 : -10,
									left: isSmallScreen ? "30%" : "50%",
								}}
							>
								<SabiasQue
									icono={leadenhall.sabiasQue[0].icono}
									texto={leadenhall.sabiasQue[0].texto}
								/>
							</Box>
						)}
					</Box>
				)}

				{/* Catedral de San Pablo */}
				{(sanPablo?.title || sanPablo?.body) && (
					<Box
						data-block-id="text-3"
						sx={{
							backgroundColor: "#f4e4df",
							padding: "12px",
							borderRadius: "12px",
							display: "flex",
							gap: "16px",
							alignItems: "center",
							position: "relative",
						}}
					>
						<Box sx={{ width: "100%" }}>
							{sanPablo?.title && (
								<Box component="h3" sx={h3}>
									{sanPablo.title}
								</Box>
							)}
							{sanPablo?.body && (
								<Box component="p" sx={p}>
									{sanPablo.body}
								</Box>
							)}
						</Box>
						{sanPablo?.sabiasQue?.[0] && (
							<Box sx={{ position: "absolute", bottom: -20, left: "50%" }}>
								<SabiasQue
									icono={sanPablo.sabiasQue[0].icono}
									texto={sanPablo.sabiasQue[0].texto}
								/>
							</Box>
						)}
					</Box>
				)}

				{/* Puente del Milenio */}
				{(puente?.title || puente?.body) && (
					<Box
						data-block-id="text-4"
						sx={{
							display: "flex",
							gap: "16px",
							position: "relative",
							flexDirection: "row-reverse",
						}}
					>
						{puenteImage && (
							<img
								src={puenteImage}
								alt={puente?.title}
								style={{
									width: "40%",
									height: 110,
									borderRadius: 12,
									objectFit: "cover",
								}}
							/>
						)}
						<Box sx={{ width: "60%", position: "relative" }}>
							{puente?.title && (
								<Box component="h3" sx={h3}>
									{puente.title}
								</Box>
							)}
							{puente?.body && (
								<Box component="p" sx={p}>
									{puente.body}
								</Box>
							)}
						</Box>
						{puente?.sabiasQue?.[0] && (
							<Box sx={{ position: "absolute", bottom: -10, left: "50%" }}>
								<SabiasQue
									icono={puente.sabiasQue[0].icono}
									texto={puente.sabiasQue[0].texto}
								/>
							</Box>
						)}
					</Box>
				)}

				{/* Tate Modern */}
				{(tateModern?.title || tateModern?.body) && (
					<Box
						data-block-id="text-5"
						sx={{
							display: "flex",
							gap: "16px",
							position: "relative",
							marginTop: isSmallScreen ? 0 : "-10px",
						}}
					>
						<Box sx={{ position: "relative", width: "100%" }}>
							{tateModern?.title && (
								<Box component="h3" sx={h3}>
									{tateModern.title}
								</Box>
							)}
							{tateModern?.body && (
								<Box component="p" sx={p}>
									{tateModern.body}
								</Box>
							)}
						</Box>
						{tateModern?.sabiasQue?.[0] && (
							<Box sx={{ position: "absolute", top: -10, left: "33%" }}>
								<SabiasQue
									icono={tateModern.sabiasQue[0].icono}
									texto={tateModern.sabiasQue[0].texto}
								/>
							</Box>
						)}
					</Box>
				)}

				{/* Shakespeare's Globe */}
				{(shakespeare?.title || shakespeare?.body) && (
					<Box data-block-id="text-6" sx={{ display: "flex", gap: "16px", position: "relative" }}>
						{shakespeareImage && (
							<img
								src={shakespeareImage}
								alt={shakespeare?.title}
								style={{
									width: "40%",
									height: 110,
									borderRadius: 12,
									objectFit: "cover",
								}}
							/>
						)}
						<Box
							sx={{
								width: "60%",
								gap: "12px",
								display: "flex",
								flexDirection: "column",
							}}
						>
							<Box>
								{shakespeare?.title && (
									<Box component="h3" sx={h3}>
										{shakespeare.title}
									</Box>
								)}
								{shakespeare?.body && (
									<Box component="p" sx={p}>
										{shakespeare.body}
									</Box>
								)}
							</Box>
						</Box>
						{shakespeare?.sabiasQue?.[0] && (
							<Box sx={{ position: "absolute", top: 0, left: "35%" }}>
								<SabiasQue
									icono={shakespeare.sabiasQue[0].icono}
									texto={shakespeare.sabiasQue[0].texto}
								/>
							</Box>
						)}
					</Box>
				)}
			</Box>
		</Box>
	);
};

Template113.thumbnail = "/images/template-1.png";

export default Template113;
